import Swiper, { baseSwiperConfig } from '@/components/Common/Swiper/Swiper';
import { Kinesis } from '@/services/TrackerService';
import { ILogFields } from '@/services/TrackerService/Kinesis/interfaces';
import type { SwiperInstance } from '@yanolja-org/react-swiper';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import styles from './ChipList.module.scss';

type CustomTags = {
  link: JSX.IntrinsicElements['a'];
  button: JSX.IntrinsicElements['button'];
  div: JSX.IntrinsicElements['div'];
};

/**
 * 칩 아이템
 */
export interface IChipItem<T extends keyof CustomTags = 'div'> {
  /** 칩 ID */
  id: string;
  /** 칩명 */
  title: string;
  /** click log meta (없는 경우 안남김) */
  logMeta?: Partial<ILogFields>;
  /** 커스텀 태그 */
  as?: T;
  /** 커스텀 태그 attributes */
  attributes?: Omit<CustomTags[T], 'ref'>;
}

/**
 * 칩 리스트 컴포넌트 Props
 */
export interface IChipListProps {
  /** chip 아이템 배열 */
  items: IChipItem<keyof CustomTags>[];
  /** default는 2*/
  minItemCountToShow?: number;
  /** 변경 할 chip index */
  activeIndex?: number;
  /** 변경 할 chip id */
  activeId?: string;
  /** slide 앞뒤 offset 공간 */
  slidesOffset?: number;
  /** slide 애니메이션 duration */
  slidesDuration?: number;
  /** 커스텀 class name */
  className?: string;
  /** chip click event callback */
  onChipClick?: (item: IChipItem<keyof CustomTags>, index: number) => void;
}

/**
 * 위젯에 사용되는 칩 리스트 컴포넌트
 *
 * @param {IChipListProps} {@link IChipListProps}
 */
const ChipList: React.FC<IChipListProps> = ({
  className,
  items,
  activeIndex = 0,
  activeId,
  slidesOffset = 0,
  slidesDuration = 300,
  onChipClick,
  minItemCountToShow = 2,
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(activeIndex);
  const swiper = useRef<SwiperInstance>(null);
  const chipsRef = useRef<(HTMLElement | null)[]>([]);
  const onTransitionEnd = (): void => {
    if (swiper.current) {
      // 터치로 swipe하는 경우  duration이 0으로 초기화됨
      // transition-duration 재설정
      swiper.current.$wrapperEl.transition(slidesDuration);
    }
  };
  const swiperConfig = useMemo(
    () => ({
      ...baseSwiperConfig,
      rebuildOnUpdate: false,
      itemType: 'tab',
      centeredSlides: false,
      spaceBetween: 8,
      slidesOffsetBefore: slidesOffset,
      slidesOffsetAfter: slidesOffset,
      threshold: 4,
      on: {
        transitionEnd: onTransitionEnd,
      },
    }),
    [slidesOffset],
  );
  const onInternalChipClick = async (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    cb?: React.MouseEventHandler<Element>,
  ): Promise<void> => {
    const index = Number(e.currentTarget.dataset.index);
    const item = items[index];
    const logMeta = item.logMeta;
    if (logMeta) {
      Kinesis.click(logMeta.pageName ?? '', logMeta);
    }
    if (cb) {
      e.persist();
      await cb(e);
      if (e.isPropagationStopped()) return;
    }
    setSelectedIndex(index);
    onChipClick?.(item, index);
    e.stopPropagation();
  };

  // selectedIndex 변경 시 translate 처리
  useEffect(() => {
    const chip = chipsRef.current?.[selectedIndex];

    if (!swiper.current || !swiper.current.wrapperEl || !chip) {
      return;
    }
    const innerWidth = swiper.current.width;
    const wrapperWidth = swiper.current.wrapperEl.scrollWidth;
    const selectedChipLeft = chip.offsetLeft;
    const selectedChipWidth = chip.offsetWidth;
    if (wrapperWidth <= innerWidth) {
      return;
    }
    if (innerWidth / 2 > selectedChipLeft + selectedChipWidth / 2) {
      swiper.current.setTranslate(slidesOffset);
      return;
    }
    if (wrapperWidth - innerWidth / 2 < selectedChipLeft + selectedChipWidth) {
      swiper.current.setTranslate(-(wrapperWidth - innerWidth + 20));
      return;
    }
    swiper.current.setTranslate(-(selectedChipLeft + selectedChipWidth / 2 - innerWidth / 2));
  }, [selectedIndex, chipsRef, swiper]);

  // selectedIndex 변경
  useEffect(() => {
    if (activeIndex === selectedIndex) {
      return;
    }
    setSelectedIndex(activeIndex);
  }, [activeIndex]);

  useEffect(() => {
    if (!activeId) {
      return;
    }
    const index = items.findIndex((item) => item.id === activeId);
    if (index !== -1 && index !== selectedIndex) {
      setSelectedIndex(index);
    }
  }, [activeId, items]);

  // transition-duration 설정
  useEffect(() => {
    if (swiper.current) {
      swiper.current.$wrapperEl.transition(slidesDuration);
    }
  }, [swiper.current]);

  if (items.length < minItemCountToShow) {
    return null;
  }

  return (
    <Swiper
      className={clsx(styles.container, className)}
      {...swiperConfig}
      getSwiper={(instance) => (swiper.current = instance)}
    >
      {items.map(({ title, id, as = 'div', attributes }, index) => {
        const ItemTag = as === 'link' ? 'a' : as;
        return (
          <ItemTag
            {...attributes}
            key={`${id}_${index}`}
            data-index={index}
            className={clsx(styles.chip, index === selectedIndex && styles.active, attributes?.className)}
            onClick={(e) => onInternalChipClick(e, attributes?.onClick)}
            {...({ ref: (ref: HTMLElement) => (chipsRef.current[index] = ref) } as any)}
          >
            {title}
          </ItemTag>
        );
      })}
    </Swiper>
  );
};
export default ChipList;
